import { useCallback, useContext, useEffect, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import { StoreContext } from '@/store/Root.store'
import { PostStartVideoResponse, EID_ERROR_CODES_RETRY } from '@/constants/videoId'
import { CANCEL_SURVEY_VIDEO_ID } from '@/constants/cancelSurvey'
import { actionReport } from '@/methods/actionReport'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { handleActionButtonsInject } from '@/methods/injectHTMLVideoID'
import { buttonsHandler } from '@/methods/keyboardAccessibility'
import { usePhoneHandler } from '@/hooks/usePhoneHandler'
import { VideoIdInstance } from '@/types/videoId'
import {
  callComplete,
  handleContext,
  handleOverlay,
  redirectFromVideoOnError,
  retryOnClick,
} from './methods'
import { API_URL } from '@/constants/apiUrl'
import { useKeyboardAwareViewportOnce } from '@/hooks/useKeyboardAwareViewportOnce'

interface UseVideoIdConfig {
  /**
   * Custom handler for video completion
   * If provided, replaces the default completion behavior entirely
   */
  onCompleted?: () => void | Promise<void>
  /**
   * Custom handler for video failure
   * If provided, replaces the default failure behavior
   */
  onFailed?: (errorCode: string) => void
  /**
   * Whether to check for retry error codes (used by VideoIdPage)
   * When true, only certain error codes trigger retry UI
   */
  checkRetryErrorCodes?: boolean
  /**
   * Whether to log VIDEO_STARTED event (used by VideoIdEnpalPage)
   */
  logVideoStarted?: boolean
}

export const useVideoId = (config: UseVideoIdConfig = {}) => {
  // Handle authentication
  const handleAuth = async () => {
    try {
      const { data } = await axiosInstance.post<PostStartVideoResponse>(
        `${API_URL}/electronic-id/start-video`,
        {},
        {
          withCredentials: true,
        }
      )

      await getPhone()
      await handleContext()

      if (logVideoStarted) {
        actionReport({
          type: 'event.onboarding-web.eidv.VIDEO_STARTED',
          payload: {},
        })
      }

      mountEid(data)
    } catch (e) {
      devLog(e)
    }
  }

  const { onCompleted, onFailed, checkRetryErrorCodes = true, logVideoStarted = false } = config

  const viewport = useKeyboardAwareViewportOnce()
  const store = useContext(StoreContext)
  const { context } = store.ScenarioState
  const { language } = store.TranslationsState
  const { videoIdPage: trans } = store.TranslationsState.translationsNew
  const { userAgent } = store.AppState
  const { system } = userAgent

  const cancelSurveyScreenEnabledServiceList = context?.cancelSurveyScreenEnabledServiceList || []

  const [overlayHandled, setOverlayHandled] = useState(false)
  const [eidError, setEidError] = useState({ errorCode: '', errorId: '' })
  const [afterClosePopupVisible, setAfterClosePopupVisible] = useState(false)
  const [isPermissionCheckSuccessful, setIsPermissionCheckSuccessful] = useState(false)
  const [shouldHideEidv, setShouldHideEidv] = useState(false)

  let videoId: VideoIdInstance | null = null

  const { getPhone, handlePhoneNumber, phoneHandled, handleOtpFocus, otpFocusHandled } =
    usePhoneHandler()

  const mountEid = (startVideoRes: PostStartVideoResponse): void => {
    const {
      electronicIdAuthorization: authToken,
      electronicIdDocType: docType,
      isBiometricConsentAccepted: eIDBiometricConsent,
    } = startVideoRes

    if (authToken === '') {
      devLog('missing videoid authentication')
      return
    }

    const eid = window.EID
    videoId = eid.videoId('#video', {
      lang: language,
    })

    videoId.start({
      authorization: authToken,
      idType: docType,
      eIDBiometricConsent,
      skipPermissionCheck: true,
    })

    videoId.on('completed', async () => {
      videoId?.turnOff()

      if (onCompleted) {
        // Custom completion behavior (EnrollmentVideoIdPage)
        await onCompleted()
      } else {
        // Default completion behavior (VideoIdPage & VideoIdEnpalPage)
        await callComplete()

        await axiosInstance.post(
          `${API_URL}/electronic-id/receive-status-from-client`,
          { result: 'complete' },
          { withCredentials: true }
        )

        forceRedirect(`${API_URL}/electronic-id/redirect-from-video?result=complete`)
      }
    })

    videoId.on('failed', async function (error) {
      if (error) {
        const errorCode = error.error?.error || 'Unknown.Error'
        const errorId = error.error?.errorId || ''

        setEidError({
          errorCode,
          errorId,
        })

        if (onFailed) {
          // Custom failure behavior
          onFailed(errorCode)
        } else if (checkRetryErrorCodes) {
          // VideoIdPage specific behavior
          if (EID_ERROR_CODES_RETRY.includes(errorCode)) {
            // Will trigger retry UI via useEffect
            return
          }

          // Non-retry error codes - hide and redirect
          setShouldHideEidv(true)

          await redirectFromVideoOnError({ errorCode, errorId })
        }
        // For VideoIdEnpalPage & EnrollmentVideoIdPage - error will trigger retry UI via useEffect
      }
    })

    videoId.on('notification', async function (event) {
      await axiosInstance.post(`${API_URL}/electronic-id/store-sdk-notification`, event, {
        withCredentials: true,
      })
    })

    videoId.on('phaseStarting', function (phase) {
      devLog('Phase starting: ' + phase.name)
      if (phase.name === 'Captcha') {
        // WebOTP API: autofill code into external widget's first input
        if ('OTPCredential' in window) {
          let abortController: AbortController | null = null
          let cancelled = false

          const simulateTyping = (
            digits: string[],
            inputs: NodeListOf<HTMLInputElement>,
            idx = 0
          ) => {
            if (idx >= digits.length || idx >= inputs.length) return
            const input = inputs[idx]
            const digit = digits[idx]
            input.focus()
            // Dispatch keydown, keypress, input, keyup events
            input.dispatchEvent(new KeyboardEvent('keydown', { bubbles: true, key: digit }))
            input.dispatchEvent(new KeyboardEvent('keypress', { bubbles: true, key: digit }))
            input.value = digit
            input.dispatchEvent(new Event('input', { bubbles: true }))
            input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, key: digit }))
            setTimeout(() => {
              simulateTyping(digits, inputs, idx + 1)
            }, 30)
          }

          const getOTP = async () => {
            abortController = new AbortController()
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const content = await (navigator.credentials as any).get({
                otp: { transport: ['sms'] },
                signal: abortController.signal,
              })
              if (cancelled) return
              if (content && 'code' in content) {
                const code = content.code
                // Try to inject code into the first OTP input
                const tryInject = () => {
                  const inputs = document.querySelectorAll<HTMLInputElement>(
                    'input.eid-textbox-character'
                  )
                  if (inputs.length >= 6) {
                    const digits = code.split('')
                    simulateTyping(digits, inputs)
                  } else {
                    // Retry until widget is loaded
                    setTimeout(tryInject, 300)
                  }
                }
                tryInject()
              }
            } catch (err) {
              devLog('OTP retrieval failed or aborted', err)
              // Ignore abort or errors
            }
          }
          getOTP()
        }
      }
      phase.continue()
    })
  }

  const handleClosePopup = useCallback(() => {
    forceRedirect(`${API_URL}/electronic-id/redirect-from-video?result=fail&error=${eidError}`)
  }, [eidError])

  const closeOnClick = async () => {
    actionReport({
      type: 'event.onboarding-web.eidv.USER_CLOSED_MANUALLY',
      payload: {},
    })
    if (cancelSurveyScreenEnabledServiceList.some((cs) => CANCEL_SURVEY_VIDEO_ID.includes(cs))) {
      setAfterClosePopupVisible(true)
    } else {
      await redirectFromVideoOnError(eidError)
    }
  }

  // Effects
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('.eid-notification-centered-content')
    const elOverlay = document.querySelector<HTMLElement>('.eid-ui-overlay')

    if (!el) return
    const overlayHeight = elOverlay?.offsetHeight
    const overlayAndroid = `${overlayHeight ? overlayHeight - 100 : 0}px`

    el.classList.toggle('keyboard-open', viewport.keyboardHeight > 0)
    if (viewport.keyboardHeight > 0)
      el.style.setProperty('height', `${system === 'ios' ? '100%' : overlayAndroid}`)
    else el.style.removeProperty('height')
  }, [viewport.keyboardHeight])

  useEffect(() => {
    if (!eidError.errorCode) return

    if (!checkRetryErrorCodes || EID_ERROR_CODES_RETRY.includes(eidError.errorCode)) {
      handleActionButtonsInject(trans, closeOnClick, retryOnClick)
    }

    setEidError({
      errorCode: '',
      errorId: '',
    })
  }, [eidError.errorCode, checkRetryErrorCodes])

  useEffect(() => {
    if (isPermissionCheckSuccessful) {
      handleAuth()
    }
  }, [isPermissionCheckSuccessful])

  // Intervals
  useInterval(
    () => {
      document.querySelector('.eid-textbox') && handlePhoneNumber()
    },
    !phoneHandled ? 300 : null
  )

  useInterval(
    () => {
      handleOtpFocus()
    },
    !otpFocusHandled ? 300 : null
  )

  useInterval(
    () => {
      document.querySelector('.eid-overlay') && handleOverlay(setOverlayHandled)
    },
    !overlayHandled ? 300 : null
  )

  useInterval(() => {
    document.querySelector('.eid-overlay') && buttonsHandler()
  }, 300)

  return {
    // State that the hook manages
    eidError,
    afterClosePopupVisible,
    isPermissionCheckSuccessful,
    shouldHideEidv,
    // Setters needed by components
    setIsPermissionCheckSuccessful,
    // Handlers
    handleClosePopup,
  }
}
